import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star,
  Flame,
  Trophy,
  CheckCircle2,
  TrendingUp,
  Target,
  ArrowRight,
} from "lucide-react";
import type { Attempt, Mastery } from "@/core/data/types";
import { getAllAttempts, getAllMastery, xpForLevel } from "@/core/data/db";
import { useProgressStore } from "@/store/progressStore";
import { useCategories, categoryName } from "@/store/customContentStore";

// Local-day key (not UTC) so buckets line up with the local weekday labels and an
// evening attempt lands on the day the user actually did it.
function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Build the last `n` day buckets (oldest → newest) with attempt counts. */
function activityBuckets(attempts: Attempt[], n: number) {
  const counts = new Map<string, number>();
  for (const a of attempts) counts.set(dayKey(new Date(a.createdAt)), (counts.get(dayKey(new Date(a.createdAt))) ?? 0) + 1);
  const today = new Date();
  const out: { key: string; label: string; count: number }[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = dayKey(d);
    out.push({
      key,
      label: d.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 2),
      count: counts.get(key) ?? 0,
    });
  }
  return out;
}

export default function Dashboard() {
  const progress = useProgressStore((s) => s.progress);
  const levelProgress = useProgressStore((s) => s.levelProgress());
  const categories = useCategories();
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [mastery, setMastery] = useState<Mastery[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([getAllAttempts(), getAllMastery()]).then(([a, m]) => {
      setAttempts(a);
      setMastery(m);
      setLoaded(true);
    });
  }, []);

  const activity = useMemo(() => activityBuckets(attempts, 14), [attempts]);
  const activityMax = Math.max(1, ...activity.map((d) => d.count));

  const masterySorted = useMemo(
    () => [...mastery].sort((a, b) => b.attempts - a.attempts),
    [mastery]
  );
  const masteryMax = Math.max(1, ...masterySorted.map((m) => m.attempts));

  const focusAreas = useMemo(() => {
    const practiced = new Set(mastery.map((m) => m.categoryId));
    return categories.filter((c) => !practiced.has(c.id)).slice(0, 6);
  }, [mastery, categories]);

  const avgScore = useMemo(() => {
    const scored = attempts.filter((a) => typeof a.score === "number");
    if (!scored.length) return null;
    return scored.reduce((s, a) => s + (a.score ?? 0), 0) / scored.length;
  }, [attempts]);

  const xpToNext = xpForLevel(progress.level + 1) - progress.xp;

  // The stored streak is only recomputed on completion, so it can be stale on load.
  // A streak is still "alive" only if the last active day was today or yesterday
  // (compared in UTC, matching how lastActiveDate is recorded); otherwise it's broken.
  const displayStreak = useMemo(() => {
    const last = progress.lastActiveDate;
    if (!last) return 0;
    const todayUtc = new Date().toISOString().slice(0, 10);
    const diff = Math.round(
      (Date.parse(`${todayUtc}T00:00:00Z`) - Date.parse(`${last}T00:00:00Z`)) / 86_400_000
    );
    return diff >= 0 && diff <= 1 ? progress.streakDays : 0;
  }, [progress.lastActiveDate, progress.streakDays]);

  if (loaded && progress.totalCompleted === 0) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-surface/60 p-10 text-center">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-surface-2 text-primary">
            <TrendingUp className="h-8 w-8" />
          </div>
          <h2 className="font-display text-2xl font-semibold text-text">Your dashboard is waiting</h2>
          <p className="text-muted">
            Complete a challenge or interview answer and your XP, streaks, and mastery will start
            charting here.
          </p>
          <Link
            to="/challenge"
            className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.02]"
          >
            Start your first challenge <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-2xl font-semibold text-text">Dashboard</h2>
        <p className="text-sm text-muted">Your growth across every discipline.</p>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Trophy} label="Level" value={progress.level} accent="primary">
          <div className="mt-3">
            <div className="h-1.5 overflow-hidden rounded-full bg-surface-2">
              <motion.div
                className="h-full rounded-full bg-primary"
                initial={{ width: 0 }}
                animate={{ width: `${Math.round(levelProgress * 100)}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            </div>
            <p className="mt-1.5 text-xs text-muted">{xpToNext} XP to level {progress.level + 1}</p>
          </div>
        </StatCard>
        <StatCard icon={Star} label="Total XP" value={progress.xp.toLocaleString()} accent="primary" />
        <StatCard icon={Flame} label="Day streak" value={displayStreak} accent="accent" />
        <StatCard icon={CheckCircle2} label="Completed" value={progress.totalCompleted} accent="accent">
          {avgScore !== null && (
            <p className="mt-3 text-xs text-muted">Avg self-assessment {avgScore.toFixed(0)}%</p>
          )}
        </StatCard>
      </div>

      {/* Activity */}
      <section className="rounded-2xl border border-border bg-surface p-5">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="h-[18px] w-[18px] text-accent" />
          <h3 className="font-display font-semibold text-text">Activity</h3>
          <span className="text-xs text-muted">last 14 days</span>
        </div>
        <div className="flex items-end justify-between gap-1.5" style={{ height: 120 }}>
          {activity.map((d) => (
            <div key={d.key} className="flex flex-1 flex-col items-center justify-end gap-1.5">
              <div className="flex w-full items-end justify-center" style={{ height: 90 }}>
                <motion.div
                  className="w-full max-w-[28px] rounded-md bg-primary/70"
                  initial={{ height: 0 }}
                  animate={{ height: `${(d.count / activityMax) * 100}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  title={`${d.count} on ${d.key}`}
                  style={{ minHeight: d.count > 0 ? 4 : 0 }}
                />
              </div>
              <span className="text-[10px] text-muted">{d.label}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Mastery */}
        <section className="rounded-2xl border border-border bg-surface p-5">
          <div className="mb-4 flex items-center gap-2">
            <Trophy className="h-[18px] w-[18px] text-primary" />
            <h3 className="font-display font-semibold text-text">Mastery by discipline</h3>
          </div>
          {masterySorted.length === 0 ? (
            <p className="text-sm text-muted">Practice a discipline to start building mastery.</p>
          ) : (
            <div className="space-y-3">
              {masterySorted.slice(0, 8).map((m) => (
                <div key={m.categoryId}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="truncate text-text">{categoryName(m.categoryId)}</span>
                    <span className="shrink-0 text-muted">{m.attempts}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-surface-2">
                    <motion.div
                      className="h-full rounded-full bg-primary"
                      initial={{ width: 0 }}
                      animate={{ width: `${(m.attempts / masteryMax) * 100}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Focus areas */}
        <section className="rounded-2xl border border-border bg-surface p-5">
          <div className="mb-4 flex items-center gap-2">
            <Target className="h-[18px] w-[18px] text-accent" />
            <h3 className="font-display font-semibold text-text">Focus areas</h3>
            <span className="text-xs text-muted">not practiced yet</span>
          </div>
          {focusAreas.length === 0 ? (
            <p className="text-sm text-muted">
              You've touched every discipline — strong coverage. Keep deepening your favorites.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {focusAreas.map((c) => (
                <Link
                  key={c.id}
                  to="/challenge"
                  className="rounded-lg border border-border bg-surface-2 px-3 py-1.5 text-sm text-muted transition-colors hover:border-primary/50 hover:text-text"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          )}
          <Link
            to="/history"
            className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary"
          >
            View full history <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
  children,
}: {
  icon: typeof Star;
  label: string;
  value: string | number;
  accent: "primary" | "accent";
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-5">
      <div className="flex items-center gap-2 text-muted">
        <Icon className={`h-[18px] w-[18px] ${accent === "primary" ? "text-primary" : "text-accent"}`} />
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 font-display text-3xl font-bold text-text">{value}</div>
      {children}
    </div>
  );
}
